import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import { noop } from '@prisjakt/ui-base';

import Rating from 'src/components/rating';
import StarIconsComponent from 'src/components/rating/star-icons';
import ErrorBoundary from 'src/components/error-boundary';
import SentimentBadge from 'src/components/sentiment-badge';
import { ProductMedia } from '../../../product-media';
import PriceHistorySummary from '../../../price-history/price-history-summary';
import { ProductFilters } from 'src/areas/product/components/product-filters';
import { LinkToPriceHistory } from '../link-to-price-history';
import { LinkToReviews } from '../LinkToReviews';
import { RankBadge } from './rank-badge';
import CampaignBadge from 'src/components/campaign-badge';
import { headerClickEvent } from '../../../../actions/product';

import { getProductMedia } from '../../utils';

import { OurChoiceProps, ProductHeaderProps } from '../../types';
import { Category, Maybe } from 'src/types';

import * as S from './styles';
import { OurChoice } from '../../../price-list/price-row/row-types/our-choice';
import { useOurChoiceExperiment } from 'src/hooks/experiments';
import RatingHelper from 'src/components/rating/rating-helper';

const ProductHeader: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<ProductHeaderDesktopProps>
>> = props => {
  const {
    className,
    title,
    product,
    currentCampaign,
    dispatchHeaderClickEvent,
    ourChoice,
    ourChoiceOffer,
    ourChoicePriceDisplay,
  } = props;

  const { t } = useAppContext();

  const [
    ourChoiceExperimentIsEnabled,
    setOurChoiceExperimentIsEnabled,
  ] = useState(false);
  const ourChoiceExperiment = useOurChoiceExperiment();

  useEffect(() => {
    setOurChoiceExperimentIsEnabled(ourChoiceExperiment);
  }, [ourChoiceExperiment]);

  if (!product) {
    return null;
  }

  const headerStyle = { minWidth: '100%' };

  const getDisplayProperties = (nodes: any) =>
    nodes.map((node: any) => node.prettyVerbose).filter((item: any) => item);

  const displayProperties =
    product && product.coreProperties
      ? getDisplayProperties(product.coreProperties.nodes)
      : [];

  const handlePriceHistoryClick = () =>
    dispatchHeaderClickEvent('price-history');

  const renderProductSummary = () => {
    if (!product) return null;
    const isLoading = !product.statistics;
    const hasPriceHistoryData =
      product.statistics &&
      product.statistics.nodes &&
      product.statistics.nodes.length > 0 &&
      product.statistics.pageInfo &&
      product.statistics.pageInfo.lowestPrice;
    const hasPriceHistory = Boolean(
      product &&
        product.statistics &&
        product.statistics.nodes &&
        product.statistics.nodes.length > 1
    );

    return (
      <S.ProductSummary>
        {(isLoading || hasPriceHistoryData) && (
          <LinkToPriceHistory onClick={handlePriceHistoryClick}>
            {(isLoading || hasPriceHistory) && (
              <PriceHistorySummary product={product} isLoading={isLoading} />
            )}
          </LinkToPriceHistory>
        )}
        <S.DesktopActionButtons product={product} />
        <S.TabletActionButtons product={product} />
      </S.ProductSummary>
    );
  };

  return (
    <S.DesktopHeader className={className} data-test="DesktopProductHeader">
      <S.TabletTitle style={headerStyle} data-test="ProductTitle">
        <S.Title outer>{title}</S.Title>
      </S.TabletTitle>
      <S.Media>
        <ErrorBoundary onError={noop}>
          <S.Badges>
            {currentCampaign ? (
              <CampaignBadge
                colors={currentCampaign.media.colors}
                size="large"
              />
            ) : null}
            {product.category &&
              product.popularity &&
              Boolean(product.popularity.inCategory) && (
                <RankBadge rank={product.popularity.inCategory} isExperiment />
              )}
          </S.Badges>
        </ErrorBoundary>
        <ErrorBoundary onError={noop}>
          {product.media && (
            <ProductMedia
              name={product.name}
              media={getProductMedia(product.media)}
              category={product.category}
            />
          )}
        </ErrorBoundary>
      </S.Media>

      <S.Content>
        <S.DesktopTitle data-test="ProductTitle">
          <S.Title inner>{title}</S.Title>
        </S.DesktopTitle>
        <ErrorBoundary onError={noop}>
          <>
            <S.More>
              <S.RatingWrapper>
                <LinkToReviews>
                  {product.userReviewSummary &&
                  product.userReviewSummary.count !== 0 ? (
                    <Rating
                      hide={product.userReviewSummary.count === 0}
                      rate={product.userReviewSummary.rating}
                      count={product.userReviewSummary.count}
                      size="large"
                      showRate
                      showRateAs="number"
                      showCounterAs="number"
                      linked
                    />
                  ) : (
                    <S.WriteReview>
                      <StarIconsComponent
                        stars={[1]}
                        rating={RatingHelper.convert(0)}
                        halfStar={false}
                      />
                      {t('reviews', 'button.write-review')}
                    </S.WriteReview>
                  )}
                </LinkToReviews>
              </S.RatingWrapper>
              {Boolean(displayProperties.length) && (
                <S.DisplayProperties
                  toneDown
                  maxrows={3}
                  properties={displayProperties
                    // Cut off at max three core properties
                    .slice(0, 3)}
                />
              )}

              {product.reviewBadges && product.reviewBadges.length > 0 ? (
                <S.ReviewBadges>
                  {product.reviewBadges.slice(0, 3).map((badge: any) => (
                    <SentimentBadge
                      badge={badge}
                      key={badge.id}
                      showNrOfReviewsAs="text"
                    />
                  ))}
                </S.ReviewBadges>
              ) : (
                ''
              )}
            </S.More>

            <ProductFilters
              category={product.category as Maybe<Category>}
              priceListStatistics={product.prices?.priceListStatistics}
              variantGroups={product.variantGroups}
              variants={product.prices?.variantValues}
            />

            {ourChoiceExperimentIsEnabled && ourChoice && (
              <S.OurChoiceContainer>
                <OurChoice
                  ourChoice={ourChoice}
                  product={product}
                  priceDisplay={ourChoicePriceDisplay}
                  campaign={currentCampaign}
                  offer={ourChoiceOffer}
                  showExpandButton={false}
                />
              </S.OurChoiceContainer>
            )}
          </>
        </ErrorBoundary>
      </S.Content>

      {renderProductSummary()}

      {product.brand && product.brand.featured && (
        <S.StoreLogo
          title={t('product', 'store-info.more-about-store', {
            store_name: product.brand.name,
          })}
          to={product.brand.pathName}
        >
          {product.brand.featured && product.brand.logo ? (
            <img src={product.brand.logo} alt={product.brand.name} />
          ) : (
            product.brand.name
          )}
        </S.StoreLogo>
      )}
    </S.DesktopHeader>
  );
};

type DispatchProps = {
  dispatchHeaderClickEvent: typeof headerClickEvent;
};
type ProductHeaderDesktopProps = ProductHeaderProps &
  DispatchProps &
  OurChoiceProps;
const dispatchProps = {
  dispatchHeaderClickEvent: headerClickEvent,
};

export default connect(null, dispatchProps)(ProductHeader);
